import React, { useContext, useState, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form } from 'antd';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { FormInputNew } from 'components/Input/FormInputNew';
import { ModalSentSuccessfullyNew } from 'components/ModalWindows/ModalSentSuccessfullyNew';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import Text from 'components/Text/Text';
import { AuthLayout } from '../components/AuthLayout';
import style from '../components/AuthFormCard.module.scss';

const passwordRegexp = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+$/g;

type ValidateStatus = Parameters<typeof Form.Item>[0]['validateStatus'];
type ComponentState = 'waitingDataEntry' | 'waitingServerResponse' | 'successServerResponse' | 'errorServerResponse';

interface IFormStatus {
	componentState: ComponentState;
	validateStatus?: ValidateStatus;
	errorMessage?: string | null;
}

type TLoginDetails = {
	password: string;
};

export const CreateNewPasswordPage: React.FC = () => {
	const { createNewPassword } = useContext<IAppContext>(AppContext);
	const [userAuthStatus, setUserAuthStatus] = useState<IFormStatus>({ componentState: 'waitingDataEntry' });
	const { t: tLogin } = useTranslation('login');
	const { t: tGlobal } = useTranslation('global');
	const history = useHistory();

	const goToLogInPage = useCallback(() => {
		history.push('/');
	}, []);

	const handleSubmitAuthData = async (loginDetails: TLoginDetails) => {
		const params = new URL(document.location.href).searchParams;
		const passwordResetToken = params.get('passwordResetToken');
		const email = params.get('email');

		setUserAuthStatus({ componentState: 'waitingServerResponse' });
		const userStatus = await createNewPassword(loginDetails.password, {
			passwordResetToken: passwordResetToken || '',
			email: email || '',
		});
		if (userStatus === 'newPassIsCreated') {
			setUserAuthStatus({ componentState: 'successServerResponse' });
		} else {
			setUserAuthStatus({
				validateStatus: 'error',
				errorMessage: tLogin('validation.serverError'),
				componentState: 'errorServerResponse',
			});
		}
	};

	const handleFieldsErrorReset = () => {
		const shouldCleanError = Object.keys(userAuthStatus).includes('validateStatus');
		if (shouldCleanError) {
			setUserAuthStatus({ componentState: 'waitingDataEntry' });
		}
	};

	const isSubmitDisabled =
		userAuthStatus.componentState === 'waitingServerResponse' ||
		userAuthStatus.componentState === 'errorServerResponse';

	return (
		<>
			{userAuthStatus.componentState === 'successServerResponse' && (
				<ModalWrapper isVisible={true}>
					<ModalSentSuccessfullyNew
						onConsentEvent={goToLogInPage}
						description="passwordChangedSuccessfully"
					/>
				</ModalWrapper>
			)}
			<AuthLayout dataTest="CreateNewPassword">
				<div className={style.formCard}>
					<Text translationKey={tLogin('passwordReset.title')} size="xl" weight="bold" />
					<Form
						name="basic"
						colon={false}
						className={style.formBody}
						onFinish={handleSubmitAuthData}
						onChange={handleFieldsErrorReset}
						layout="vertical"
						autoComplete="off"
						data-test="form">
						<div className={style.fieldsGroup}>
							<FormInputNew
								name="password"
								label="password"
								type="password"
								translationNameSpace="login"
								placeholder="password"
								validateStatus={userAuthStatus.validateStatus}
								help={userAuthStatus.errorMessage}
								validationRules={[
									{ required: true, message: tLogin('validation.enterCorrectValue.password') },
									{ min: 6, message: tLogin('validation.minNumOfChar') + '6' },
									{ max: 60, message: tLogin('validation.maxNumOfChar') + '60' },
									() => ({
										validator(_, value) {
											if (value && !value.match(passwordRegexp)) {
												return Promise.reject(
													new Error(tLogin('validation.forbiddenCharactersInPassword')),
												);
											}
											return Promise.resolve();
										},
									}),
								]}
							/>

							<FormInputNew
								name="confirm"
								label="confirmPassword"
								type="password"
								translationNameSpace="login"
								placeholder="confirmPassword"
								dependencies={['password']}
								validationRules={[
									{ required: true, message: tLogin('validation.confirmPassword') },
									({ getFieldValue }) => ({
										validator(_, value) {
											if (!value || getFieldValue('password') === value) {
												return Promise.resolve();
											}
											return Promise.reject(new Error(tLogin('validation.passwordsNotMatch')));
										},
									}),
								]}
							/>
						</div>

						<div className={style.actionsRow} data-test="btns">
							<NewButton variant="outline" color="gray" size="small" onClick={goToLogInPage}>
								{tGlobal('cancel')}
							</NewButton>
							<NewButton
								type="submit"
								variant="primary"
								color="purple"
								size="small"
								disabled={isSubmitDisabled}
								loading={userAuthStatus.componentState === 'waitingServerResponse'}>
								{tLogin('changePassword')}
							</NewButton>
						</div>
					</Form>
				</div>
			</AuthLayout>
		</>
	);
};
